const MEDIUM_RSS_URL = 'https://medium.com/feed/@propheticroundtable7';
const CACHE_KEY = 'medium_posts_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const extractImageUrl = (content) => {
  const imgRegex = /<img[^>]+src="([^">]+)"/;
  const match = content.match(imgRegex);
  return match ? match[1] : null;
};

const decodeHtmlEntities = (text) => {
  // Create a temporary div to decode HTML entities
  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || text;
  } catch (error) {
    // Fallback for basic HTML entities
    return text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
  }
};

const extractExcerpt = (content) => {
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;
  
  // Get text content and remove extra whitespace
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  const trimmedText = plainText.replace(/\s+/g, ' ').trim();
  
  // Return first 200 characters with ellipsis
  return trimmedText.substring(0, 200) + '...';
};

const getCachedPosts = () => {
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    const { posts, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < CACHE_DURATION) {
      return posts;
    }
  }
  return null;
};

const cachePosts = (posts) => {
  localStorage.setItem(CACHE_KEY, JSON.stringify({
    posts,
    timestamp: Date.now()
  }));
};

export const fetchMediumPosts = async (forceRefresh = false) => {
  try {
    console.log('MediumService: fetchMediumPosts called, forceRefresh:', forceRefresh);
    // Check cache first (unless force refresh is requested)
    if (!forceRefresh) {
      const cached = getCachedPosts();
      if (cached) {
        console.log('MediumService: Returning cached posts:', cached.length);
        return cached;
      }
    }

    // Fetch from Medium RSS feed with cache-busting
    const cacheBuster = Date.now();
    console.log('MediumService: Fetching from API...');
    const response = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(MEDIUM_RSS_URL)}&api_key=&count=10&nocache=${cacheBuster}`);
    
    if (!response.ok) {
      console.error('MediumService: API response not ok:', response.status, response.statusText);
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('MediumService: Raw API response:', data);

    if (data.status === 'ok') {
      console.log('MediumService: API status ok, processing items:', data.items?.length);
      const posts = data.items.map(item => ({
        title: decodeHtmlEntities(item.title),
        date: new Date(item.pubDate).toLocaleDateString(),
        author: item.author,
        excerpt: extractExcerpt(item.content),
        image: item.thumbnail || extractImageUrl(item.content),
        category: 'MINISTRY', // You might want to customize this
        tags: item.categories || [],
        content: item.content,
        link: item.link,
        guid: item.guid
      }));

      console.log('MediumService: Processed posts:', posts.length, posts[0]?.title);
      // Cache the posts
      cachePosts(posts);
      return posts;
    } else {
      console.error('MediumService: API returned error status:', data.status, data.message);
      throw new Error(`API error: ${data.status}`);
    }
    
    throw new Error('Failed to fetch Medium posts');
  } catch (error) {
    console.error('MediumService: Error fetching Medium posts:', error);
    // Return cached posts as fallback if available
    const cached = getCachedPosts();
    if (cached) {
      console.log('MediumService: Returning cached posts as fallback:', cached.length);
      return cached;
    }
    console.log('MediumService: No cached posts available, returning empty array');
    return [];
  }
};

export const clearMediumPostsCache = () => {
  localStorage.removeItem(CACHE_KEY);
  console.log('Medium posts cache cleared');
};

export const refreshMediumPosts = async () => {
  console.log('Manually refreshing Medium posts...');
  clearMediumPostsCache();
  return await fetchMediumPosts(true);
};

// For debugging - show cache status
export const getCacheStatus = () => {
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    const { timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;
    const remainingTime = CACHE_DURATION - age;
    return {
      cached: true,
      ageMinutes: Math.floor(age / (60 * 1000)),
      remainingMinutes: Math.floor(remainingTime / (60 * 1000)),
      expiresAt: new Date(timestamp + CACHE_DURATION).toLocaleTimeString()
    };
  }
  return { cached: false };
}; 